import { auth } from '@/state/auth'
import axios from 'axios'
import { produce } from 'immer'
import { create } from 'zustand'
import type { PresetData } from '../../functions/_utils/types'
import type { UpdatePresetParams } from '../../functions/api/presets/[id]'

type PresetsState = {
  loading: boolean
  list: PresetData[]
  reset(): void
  fetch(): Promise<void>
  create(
    name: string,
    content: string,
    defaultPresetId?: number,
  ): Promise<PresetData>
  update(id: number, name: string, content: string): Promise<PresetData>
  delete(id: number): Promise<void>
}

export const usePresetsState = create<PresetsState>((set, get) => ({
  list: [],
  loading: false,

  fetch: async () => {
    try {
      set({ loading: true })

      const { data } = await auth(token => (
        axios.get<PresetData[]>(`/api/presets`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set({ list: data })
    }
    catch (e) {
      console.error(e)
    }
    finally {
      set({ loading: true })
    }
  },

  create: async (name, content, defaultToneId) => {
    try {
      set({ loading: true })
      const { data } = await auth(token => (
        axios.post<PresetData>('/api/presets', {
          name,
          content,
          defaultToneId,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set(produce((s: PresetsState) => {
        s.list = [
          data,
          ...s.list.filter(item => item.id !== defaultToneId),
        ]
      }))

      return data
    }
    catch (e) {
      console.error(e)
      throw e
    }
    finally {
      set({ loading: false })
    }
  },

  update: async (id, name, content) => {
    try {
      set({ loading: true })

      const { data } = await auth(token => (
        axios.put<PresetData>(
          `/api/presets/${id}`,
          {
            name,
            content,
          } satisfies UpdatePresetParams,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
      ))

      await get().fetch()

      return data
    }
    catch (e) {
      console.error(e)
      throw e
    }
    finally {
      set({ loading: false })
    }
  },

  delete: async id => {
    try {
      set({ loading: true })

      await auth(token => (
        axios.delete(`/api/presets/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      await get().fetch()
    }
    catch (e) {
      console.error(e)
    }
    finally {
      set({ loading: false })
    }
  },

  reset() {
    set({
      loading: false,
      list: [],
    })
  },
}))
